import React, {
  RefObject,
  useCallback,
  useEffect,
  useId,
  useState,
} from 'react'
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'
import ColorSwatchControl from './ColorSwatchControl.component'
import './EmailTemplateFormatPanel.css'
import { normalizeBrowserSelectionInRteEditPanel } from '@utils/rteEmailSelection.util'

type RteCommandName = Parameters<RichTextEditorComponent['executeCommand']>[0]

const FONT_SIZE_OPTIONS = [
  { text: '11px · Small', value: '11px' },
  { text: '12px', value: '12px' },
  { text: '13px · Body', value: '13px' },
  { text: '14px · Body (lg)', value: '14px' },
  { text: '16px · Subtitle', value: '16px' },
  { text: '18px · Subtitle (lg)', value: '18px' },
  { text: '22px · Title', value: '22px' },
  { text: '28px · Heading', value: '28px' },
  { text: '36px · Hero', value: '36px' },
] as const

export interface EmailTemplateFormatPanelProps {
  rteRef: RefObject<RichTextEditorComponent | null>
  defaultFontSize?: string
  onDefaultFontSizeChange?: (value: string) => void
}

function focusRte(rte: RichTextEditorComponent): void {
  const r = rte as RichTextEditorComponent & { focusIn?: () => void }
  if (typeof r.focusIn === 'function') {
    r.focusIn()
  }
}

const EmailTemplateFormatPanel: React.FC<EmailTemplateFormatPanelProps> = ({
  rteRef,
  defaultFontSize = '13px',
  onDefaultFontSizeChange,
}) => {
  const colorIds = useId()
  const fontColorInputId = `${colorIds}-font`
  const backColorInputId = `${colorIds}-bg`

  const [fontSize, setFontSize] = useState<string>(defaultFontSize)
  const [fontColor, setFontColor] = useState('#1e293b')
  const [backColor, setBackColor] = useState('#ffffff')

  const exec = useCallback(
    (command: RteCommandName, value?: string): void => {
      const rte = rteRef.current
      if (!rte) return
      focusRte(rte)
      const panel = rte.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      normalizeBrowserSelectionInRteEditPanel(panel)
      const run = (): void => {
        if (value !== undefined) {
          rte.executeCommand(command, value)
        } else {
          rte.executeCommand(command)
        }
      }
      try {
        run()
      } catch {
        normalizeBrowserSelectionInRteEditPanel(panel)
        try {
          run()
        } catch {
          /* Syncfusion can throw if internal selection still references removed nodes */
        }
      }
    },
    [rteRef],
  )

  const preventTakeFocus: React.MouseEventHandler = (e) => {
    e.preventDefault()
  }

  const hasNonCollapsedSelectionInRte = useCallback((): boolean => {
    const rte = rteRef.current
    if (!rte) return false
    const panel = rte.contentModule?.getEditPanel?.() as HTMLElement | undefined
    if (!panel) return false
    const win = panel.ownerDocument.defaultView ?? window
    const sel = win.getSelection?.()
    if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return false
    try {
      return panel.contains(sel.anchorNode) && panel.contains(sel.focusNode)
    } catch {
      return false
    }
  }, [rteRef])

  useEffect(() => {
    setFontSize(defaultFontSize)
  }, [defaultFontSize])

  return (
    <div
      className="mt-tp-email-format-panel"
      role="group"
      aria-label="Text formatting"
    >
      <span className="mt-tp-settings-subheader">Text formatting</span>

      <div className="mt-tp-email-format-section">
        <div className="mt-tp-email-format-field-block">
          <div className="mt-tp-email-format-label-row mt-tp-email-format-label-row--single">
            <span className="mt-tp-email-format-label">Font size</span>
          </div>
          <div className="mt-tp-email-format-controls-row mt-tp-email-format-controls-row--full">
            <DropDownListComponent
              dataSource={[...FONT_SIZE_OPTIONS]}
              fields={{ text: 'text', value: 'value' }}
              value={fontSize}
              change={(args) => {
                const v = String(args.value ?? '13px')
                setFontSize(v)
                if (hasNonCollapsedSelectionInRte()) {
                  exec('fontSize', v)
                } else {
                  onDefaultFontSizeChange?.(v)
                }
              }}
              className="mt-tp-email-format-fontsize-dd e-caret-hide"
            />
          </div>
        </div>
        <div className="mt-tp-email-format-field-block">
          <div className="mt-tp-email-format-label-row mt-tp-email-format-label-row--single">
            <span className="mt-tp-email-format-label">Font style</span>
          </div>
          <div className="mt-tp-email-format-controls-row">
            <div className="mt-tp-email-format-style-btns">
              <button
                type="button"
                className="mt-tp-email-format-icon-btn"
                title="Bold"
                aria-label="Bold"
                onMouseDown={preventTakeFocus}
                onClick={() => exec('bold')}
              >
                <span className="ti ti-bold" />
              </button>
              <button
                type="button"
                className="mt-tp-email-format-icon-btn"
                title="Italic"
                aria-label="Italic"
                onMouseDown={preventTakeFocus}
                onClick={() => exec('italic')}
              >
                <span className="ti ti-italic" />
              </button>
              <button
                type="button"
                className="mt-tp-email-format-icon-btn"
                title="Underline"
                aria-label="Underline"
                onMouseDown={preventTakeFocus}
                onClick={() => exec('underline')}
              >
                <span className="ti ti-underline" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-tp-email-format-section">
        <div className="mt-tp-email-format-label-row mt-tp-email-format-label-row--single">
          <span className="mt-tp-email-format-label">Alignment</span>
        </div>
        <div className="mt-tp-email-format-align-row">
          <button
            type="button"
            className="mt-tp-email-format-icon-btn"
            title="Align left"
            aria-label="Align left"
            onMouseDown={preventTakeFocus}
            onClick={() => exec('justifyLeft')}
          >
            <span className="ti ti-align-left" />
          </button>
          <button
            type="button"
            className="mt-tp-email-format-icon-btn"
            title="Align center"
            aria-label="Align center"
            onMouseDown={preventTakeFocus}
            onClick={() => exec('justifyCenter')}
          >
            <span className="ti ti-align-center" />
          </button>
          <button
            type="button"
            className="mt-tp-email-format-icon-btn"
            title="Align right"
            aria-label="Align right"
            onMouseDown={preventTakeFocus}
            onClick={() => exec('justifyRight')}
          >
            <span className="ti ti-align-right" />
          </button>
          <button
            type="button"
            className="mt-tp-email-format-icon-btn"
            title="Justify"
            aria-label="Justify"
            onMouseDown={preventTakeFocus}
            onClick={() => exec('justifyFull')}
          >
            <span className="ti ti-align-justified" />
          </button>
        </div>
      </div>

      <div className="mt-tp-email-format-section">
        <div className="mt-tp-email-format-field-block">
          <label
            className="mt-tp-email-format-label"
            htmlFor={fontColorInputId}
          >
            Font color
          </label>
          <ColorSwatchControl
            id={fontColorInputId}
            value={fontColor}
            ariaLabel="Font color"
            onControlMouseDown={preventTakeFocus}
            onChange={(e) => {
              const v = e.target.value
              setFontColor(v)
              exec('fontColor', v)
            }}
          />
        </div>
        <div className="mt-tp-email-format-field-block">
          <label
            className="mt-tp-email-format-label"
            htmlFor={backColorInputId}
          >
            Background color
          </label>
          <ColorSwatchControl
            id={backColorInputId}
            value={backColor}
            ariaLabel="Background color"
            onControlMouseDown={preventTakeFocus}
            onChange={(e) => {
              const v = e.target.value
              setBackColor(v)
              exec('backColor', v)
            }}
          />
        </div>
      </div>

      <div className="mt-tp-email-format-section mt-tp-email-format-section--clear">
        <button
          type="button"
          className="mt-tp-email-format-clear-btn"
          onMouseDown={preventTakeFocus}
          onClick={() => exec('removeFormat')}
        >
          Clear formatting
        </button>
      </div>
    </div>
  )
}

export default EmailTemplateFormatPanel
